import React, { useState, useEffect } from 'react';
import './ContactTracingForm.css'; // We'll need to create this CSS file

const ContactTracingForm = ({ personaName, conversationId, onFormDataChange }) => {
    // Load initial state from localStorage or use default values
    const [caseInfo, setCaseInfo] = useState(() => {
        const saved = localStorage.getItem('contactTracingCaseInfo');
        return saved ? JSON.parse(saved) : {
            symptomOnset: '',
            symptoms: {
                weakness: '',
                muscleAches: '',
                cough: '',
                nasalCongestion: '',
                lossOfAppetite: '',
                headache: '',
                soreness: '',
                fever: ''
            },
            additionalSymptomInfo: '',
            vaccinationStatus: '',
            contractionDetails: '',
            transmissionDetails: ''
        };
    });

    const [feedback, setFeedback] = useState('');
  
    // Update parent component when form data changes
    useEffect(() => {
        if (onFormDataChange) {
            const formData = {
                personaName,
                conversationId,
                symptomOnset: caseInfo.symptomOnset || '',
                symptoms: caseInfo.symptoms || {
                    weakness: '',
                    muscleAches: '',
                    cough: '',
                    nasalCongestion: '',
                    lossOfAppetite: '',
                    headache: '',
                    soreness: '',
                    fever: ''
                },
                additionalSymptomInfo: caseInfo.additionalSymptomInfo || '',
                vaccinationStatus: caseInfo.vaccinationStatus || '',
                contractionDetails: caseInfo.contractionDetails || '',
                transmissionDetails: caseInfo.transmissionDetails || ''
            };
            onFormDataChange(formData);
        }
    }, [personaName, conversationId, caseInfo, onFormDataChange]);

    const handleCaseInfoChange = (e) => {
        setCaseInfo({ ...caseInfo, [e.target.name]: e.target.value });
    };

    const handleSymptomChange = (symptom, value) => {
        setCaseInfo({
            ...caseInfo,
            symptoms: {
                ...(caseInfo.symptoms || {
                    weakness: '',
                    muscleAches: '',
                    cough: '',
                    nasalCongestion: '',
                    lossOfAppetite: '',
                    headache: '',
                    soreness: '',
                    fever: ''
                }),
                [symptom]: value
            }
        });
    };

    // Save to localStorage whenever state changes
    useEffect(() => {
        localStorage.setItem('contactTracingCaseInfo', JSON.stringify(caseInfo));
    }, [caseInfo]);

    const clearStoredData = () => {
        localStorage.removeItem('contactTracingCaseInfo');
    };

    // Function to collect final data before submission
    const collectFinalData = () => {
        console.log(`[${new Date().toISOString()}] 📋 Collecting final contact tracing form data`);
        if (onFormDataChange) {
            const formData = {
                personaName,
                conversationId,
                symptomOnset: caseInfo.symptomOnset || '',
                symptoms: caseInfo.symptoms || {
                    weakness: '',
                    muscleAches: '',
                    cough: '',
                    nasalCongestion: '',
                    lossOfAppetite: '',
                    headache: '',
                    soreness: '',
                    fever: ''
                },
                additionalSymptomInfo: caseInfo.additionalSymptomInfo || '',
                vaccinationStatus: caseInfo.vaccinationStatus || '',
                contractionDetails: caseInfo.contractionDetails || '',
                transmissionDetails: caseInfo.transmissionDetails || ''
            };
            onFormDataChange(formData);
            console.log(`[${new Date().toISOString()}] ✅ Final contact tracing data collected successfully`);
        }
    };

    // Export method to clear data (will be called by parent when conversation ends)
    useEffect(() => {
        // Expose methods to parent through global ref
        if (!window.contactTracingFormRef) {
            window.contactTracingFormRef = {};
        }
        window.contactTracingFormRef.clearData = clearStoredData;
        window.contactTracingFormRef.collectFinalData = collectFinalData;
        
        return () => {
            if (window.contactTracingFormRef) {
                delete window.contactTracingFormRef.clearData;
                delete window.contactTracingFormRef.collectFinalData;
            }
        };
    }, [caseInfo, onFormDataChange, personaName, conversationId]);

    return (
        <div className="contact-tracing-container">
            <div id="contactTracingForm" className="contact-tracing-form">
                <div className="form-section">
                    <h3 className="section-title">Case Information</h3>
                    <div className="form-group">
                        <label htmlFor="symptomOnset">Symptom Onset Date: [DD/MM/YYYY]</label>
                        <input
                            type="date"
                            id="symptomOnset"
                            name="symptomOnset"
                            value={caseInfo.symptomOnset}
                            onChange={handleCaseInfoChange}
                            className="form-input"
                        />
                    </div>
                    
                    <h3 className="section-title">Symptoms</h3>
                    <table className="symptoms-table">
                        <thead>
                            <tr>
                                <th>Symptom</th>
                                <th>Response [Yes/No/Unsure]</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Weakness</td>
                                <td>
                                    <select
                                        value={caseInfo.symptoms?.weakness || ''}
                                        onChange={(e) => handleSymptomChange('weakness', e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="">[Yes/No/Unsure]</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                        <option value="Unsure">Unsure</option>
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td>Muscle aches</td>
                                <td>
                                    <select
                                        value={caseInfo.symptoms?.muscleAches || ''}
                                        onChange={(e) => handleSymptomChange('muscleAches', e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="">[Yes/No/Unsure]</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                        <option value="Unsure">Unsure</option>
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td>Cough</td>
                                <td>
                                    <select
                                        value={caseInfo.symptoms?.cough || ''}
                                        onChange={(e) => handleSymptomChange('cough', e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="">[Yes/No/Unsure]</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                        <option value="Unsure">Unsure</option>
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td>Nasal congestion</td>
                                <td>
                                    <select
                                        value={caseInfo.symptoms?.nasalCongestion || ''}
                                        onChange={(e) => handleSymptomChange('nasalCongestion', e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="">[Yes/No/Unsure]</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                        <option value="Unsure">Unsure</option>
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td>Loss of appetite</td>
                                <td>
                                    <select
                                        value={caseInfo.symptoms?.lossOfAppetite || ''}
                                        onChange={(e) => handleSymptomChange('lossOfAppetite', e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="">[Yes/No/Unsure]</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                        <option value="Unsure">Unsure</option>
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td>Headache</td>
                                <td>
                                    <select
                                        value={caseInfo.symptoms?.headache || ''}
                                        onChange={(e) => handleSymptomChange('headache', e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="">[Yes/No/Unsure]</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                        <option value="Unsure">Unsure</option>
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td>Soreness</td>
                                <td>
                                    <select
                                        value={caseInfo.symptoms?.soreness || ''}
                                        onChange={(e) => handleSymptomChange('soreness', e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="">[Yes/No/Unsure]</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                        <option value="Unsure">Unsure</option>
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td>Fever ({'>'}37.8)</td>
                                <td>
                                    <select
                                        value={caseInfo.symptoms?.fever || ''}
                                        onChange={(e) => handleSymptomChange('fever', e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="">[Yes/No/Unsure]</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                        <option value="Unsure">Unsure</option>
                                    </select>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    
                    <div className="form-group">
                        <label htmlFor="additionalSymptomInfo">Additional Symptom Information:</label>
                        <textarea
                            id="additionalSymptomInfo"
                            name="additionalSymptomInfo"
                            value={caseInfo.additionalSymptomInfo}
                            onChange={handleCaseInfoChange}
                            className="form-textarea"
                            placeholder="Free text field for notes about symptom severity, duration, or other relevant details"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="vaccinationStatus">Vaccination Status:</label>
                        <select
                            id="vaccinationStatus"
                            name="vaccinationStatus"
                            value={caseInfo.vaccinationStatus}
                            onChange={handleCaseInfoChange}
                            className="form-input"
                        >
                            <option value="">[Vaccinated/Not Vaccinated/Partially Vaccinated/Unknown]</option>
                            <option value="Vaccinated">Vaccinated</option>
                            <option value="Not Vaccinated">Not Vaccinated</option>
                            <option value="Partially Vaccinated">Partially Vaccinated</option>
                            <option value="Unknown">Unknown</option>
                        </select>
                </div>

                    <div className="form-group">
                        <label htmlFor="contractionDetails">Potential Contraction details:</label>
                                        <textarea
                            id="contractionDetails"
                            name="contractionDetails"
                            value={caseInfo.contractionDetails}
                            onChange={handleCaseInfoChange}
                                            className="form-textarea"
                            placeholder="Free text field for how the person likely contracted the illness"
                        />
                </div>

                    <div className="form-group">
                        <label htmlFor="transmissionDetails">Potential Transmission details:</label>
                                        <textarea
                            id="transmissionDetails"
                            name="transmissionDetails"
                            value={caseInfo.transmissionDetails}
                            onChange={handleCaseInfoChange}
                                            className="form-textarea"
                            placeholder="Free text field for who the person may have transmitted to"
                                        />
                    </div>
                </div>

                <p className="info-text">This form will be automatically submitted when you end the conversation.</p>
            </div>
            {feedback && <div className="feedback" dangerouslySetInnerHTML={{ __html: feedback }}></div>}
        </div>
    );
};

export default ContactTracingForm;